import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { MessagesButton } from "@/features/messages/MessagesButton"
import { NotificationBell } from "@/features/notifications/NotificationBell"
import { routes } from "@/routes/paths"

/**
 * The header title, derived from the route rather than hardcoded.
 *
 * The block ships `<h1>Documents</h1>` — a literal, because a Next.js app gives
 * each route its own layout and each one sets its own. A client-side router has
 * ONE header for every screen, so the title has to come from somewhere: either
 * every screen pushes one up through context, or the header reads the location.
 * Reading the location is the smaller mechanism and cannot be forgotten by a
 * screen author.
 *
 * It also keeps the title and the sidebar honest with each other — both resolve
 * the same `routes` constants and the same translation keys, so the highlighted
 * nav item and the heading can never name different things.
 *
 * The fallback is the app's own name rather than an empty bar: an unmatched
 * route is on its way to the catch-all redirect, and a blank `<h1>` during that
 * frame reads as a broken screen.
 */
const TITLE_BY_PATH: Readonly<Record<string, string>> = {
  [routes.home]: "nav.home",
  [routes.invoices]: "nav.invoices",
  [routes.quotations]: "nav.quotations",
  [routes.challans]: "nav.challans",
  [routes.customers]: "nav.customers",
  [routes.items]: "nav.items",
  [routes.reports]: "nav.reports",
  /*
   * ═════════════════════════════════════════════════════════════════════════
   * EVERY `/settings/*` SCREEN READS "Settings" HERE, DELIBERATELY.
   *
   * `routes.users` and `routes.audit` were listed separately until 2026-08-17,
   * with this reasoning: they live under `/settings/`, so the longest-prefix
   * fallback would title them "Settings" while THE RAIL HIGHLIGHTED A DIFFERENT
   * ROW — heading and highlight disagreeing.
   *
   * THERE IS NO LONGER A DIFFERENT ROW. Settings, Features, People and Activity
   * log are one rail entry now, with a `NavigationMenu` inside
   * (features/settings/SettingsLayout.tsx). So "Settings" is what the
   * highlighted row says, and the two agree BECAUSE this is generic. Naming the
   * section here would recreate the disagreement in the other direction.
   *
   * It also fixes an inconsistency nobody had noticed: `routes.features` was
   * never in this map, so Features already fell through to "Settings" while its
   * two neighbours did not. Three of five sections behaved one way and two the
   * other.
   *
   * WHICH SECTION YOU ARE ON IS ANSWERED BY THE NAV MENU, which marks the
   * current item `aria-current="page"`. That is a better answer than this
   * heading could give, because it also shows what else is there.
   * ═════════════════════════════════════════════════════════════════════════
   */
  [routes.settings]: "nav.settings",
  /*
   * NOT A `nav.*` KEY, BECAUSE THERE IS NO NAV ITEM. Every other entry here
   * names a sidebar row, so heading and highlighted item cannot disagree. The
   * hub is deliberately not in the sidebar — you arrive because the bell told
   * you to (see `routes.notifications`) — so its heading comes from the
   * feature's own strings instead, and the absence is the point rather than an
   * oversight.
   */
  [routes.notifications]: "notif.title",
  /*
   * Also not a `nav.*` key, and for the same reason as the hub above: the
   * messenger is deliberately not in the sidebar — you arrive because the badge
   * told you to (see `routes.messages`) — so its heading comes from the
   * feature's own strings.
   *
   * ONE ENTRY COVERS ALL THREE MESSENGER PATHS, and that is now correct rather
   * than a gap. `/messages/direct` used to be listed separately because it was a
   * SECOND SCREEN and the lookup below is longest-prefix-wins, so without its own
   * entry a private conversation would have been headed "Messages" — the one
   * word that could not distinguish the two. There is one screen now: the list
   * with a conversation open beside it, and `/messages/group` and `/messages/3`
   * are states of it. Which conversation you are in is answered by the pane's own
   * heading and by the highlighted row, both of which say more than this line
   * could.
   */
  [routes.messages]: "chat.title",
  /*
   * §17A.1's mailbox, and unlike the two above this one IS a `nav.*` key — it
   * has a rail row (see `app-sidebar.tsx`), so the heading and the highlighted
   * item must name the same thing. Without an entry here the fallback is the
   * app's own name, which would have printed "SteelBill" over a screen whose
   * rail row said "Mail".
   */
  [routes.mail]: "nav.mail",
}

export function SiteHeader() {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  /*
   * Longest prefix wins, so `/invoices/42` still says "Bills" rather than
   * falling through to the app name. Home is `"/"` and would prefix-match
   * everything, so it is only used on an exact match.
   */
  const key =
    TITLE_BY_PATH[pathname] ??
    Object.entries(TITLE_BY_PATH)
      .filter(([path]) => path !== routes.home && pathname.startsWith(path))
      .sort((a, b) => b[0].length - a[0].length)[0]?.[1]

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mx-2 h-4 data-vertical:self-auto" />
        <h1 className="text-base font-medium">{t(key ?? "app.name")}</h1>
        {/*
          * NO SEARCH BOX HERE. It sits in the sidebar directly above the
          * scanner, so the two ways of finding a document — type it, or scan it
          * — are one pair in one place rather than split across the screen.
          * ⌘K still opens it from anywhere; the binding lives on the provider in
          * `AppShell`, not on any button.
          */}

        {/*
          * THE BELL TAKES THE EMPTY RIGHT END, and nothing else moves.
          *
          * `ml-auto` pushes it to the edge without touching the title's
          * position, so a screen that had a header yesterday has the identical
          * header today plus one control. It is deliberately HERE and not on the
          * home screen: §11.2's four action tiles are the largest element and the
          * point of that screen, and a compliance backstop must not compete with
          * "Make a bill" for the same eye. The header is above the scroll area,
          * so the bell is also on every OTHER screen — which is where the reader
          * usually is when a bill someone else saved crosses the threshold.
          *
          * It renders NOTHING without `notification.view` and nothing while the
          * `notifications` flag is off, so this line is a no-op on a build where
          * the module is not switched on.
          */}
        {/*
          * TWO CONTROLS, ONE CORNER — and the pairing is the design rather than
          * an accident of ordering.
          *
          * The bell is a COMPLIANCE backstop; the speech bubble is a
          * CONVERSATION. They are deliberately not merged into one badge:
          * neither reading action could clear a combined count, one icon cannot
          * open two screens, and diluting "this bill needs an e-way bill" — a
          * detention if it is missed — with "ok, send it" is how the alert that
          * matters stops being seen. See `MessagesButton` for the argument in
          * full.
          *
          * The conversation sits to the LEFT of the bell, so the compliance
          * alert keeps the outermost, most-glanced-at position it has had since
          * §17.7 shipped. Both render NOTHING without their capability and
          * nothing while their flag is off, so on a build where neither module
          * is switched on this whole cluster is empty, exactly as before.
          */}
        <div className="ml-auto flex items-center gap-1">
          <MessagesButton />
          <NotificationBell />
        </div>
      </div>
    </header>
  )
}
